/**
 * EmailSuppressionBadge — surfaces email-suppression status next to a
 * contact's email address.
 *
 * Three visible states:
 *   1. Suppressed (red): contact.is_email_suppressed === true.
 *      Reason shown inline, reason_detail + first_suppressed_at on
 *      hover.
 *   2. Soft-bounce warning (yellow): no suppression row OR
 *      is_suppressed=false BUT a tracker row exists with
 *      soft_bounce_count in [1, threshold). The badge reads "Soft
 *      bounce N/3" so operators can intervene before auto-suppression.
 *   3. Hidden: clean state — no badge rendered.
 *
 * Per audit V2: the badge labels say "Email address suppressed"
 * (not "Contact suppressed"), because multiple contacts can share
 * one email and the suppression is keyed to the address.
 *
 * Data sourced denormalized for the common (suppressed) case to avoid
 * a per-contact query; falls back to a one-row lookup for the
 * sub-threshold soft-bounce case so we can show the count.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  SUPPRESSION_REASON_LABELS,
  SOFT_BOUNCE_THRESHOLD,
  type EmailSuppressionReason,
} from "../../types/emailSuppression.types";
import { getEmailSuppression } from "../../services/emailSuppression.service";

interface Props {
  email: string | null | undefined;
  isEmailSuppressed: boolean | undefined;
  suppressionReason: EmailSuppressionReason | undefined;
  suppressionDetail: string | undefined;
  suppressionUpdatedAt: string | undefined;
  /** Compact rendering for the Contacts list rows. Omits the tooltip text. */
  compact?: boolean;
}

const EmailSuppressionBadge: React.FC<Props> = ({
  email,
  isEmailSuppressed,
  suppressionReason,
  suppressionDetail,
  suppressionUpdatedAt,
  compact = false,
}) => {
  // Only fetch when we're in the "could be a tracker row" case — saves
  // a query on every contact card and list row.
  const shouldFetchSoftBounce = !isEmailSuppressed && !!email;
  const { data: suppression } = useQuery({
    queryKey: ["email-suppression", email?.toLowerCase().trim()],
    queryFn: () => getEmailSuppression(email),
    enabled: shouldFetchSoftBounce,
    staleTime: 60_000,
  });

  // Suppressed (red)
  if (isEmailSuppressed && suppressionReason) {
    const label =
      SUPPRESSION_REASON_LABELS[suppressionReason] ?? suppressionReason;
    const title = compact
      ? `Email address suppressed — ${label}`
      : [
          `Email address suppressed — ${label}`,
          suppressionDetail ? `Detail: ${suppressionDetail}` : null,
          suppressionUpdatedAt
            ? `Updated: ${new Date(suppressionUpdatedAt).toLocaleString()}`
            : null,
        ]
          .filter(Boolean)
          .join("\n");
    return (
      <span
        title={title}
        className="inline-flex items-center gap-1 rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 border border-red-200"
      >
        <svg
          className="h-3 w-3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06L3.28 2.22zM17.5 4.5l-4.95 4.95L4.5 1.49V4l9.31 9.31 3.69-3.69V4.5z"
            clipRule="evenodd"
          />
        </svg>
        {compact ? "Email suppressed" : `Email suppressed — ${label}`}
      </span>
    );
  }

  // Soft-bounce warning (yellow) — only when tracker row exists with
  // count in [1, threshold)
  const softCount = suppression?.soft_bounce_count ?? 0;
  if (
    shouldFetchSoftBounce &&
    softCount > 0 &&
    softCount < SOFT_BOUNCE_THRESHOLD &&
    !suppression?.is_suppressed
  ) {
    const title = compact
      ? `Soft bounce ${softCount}/${SOFT_BOUNCE_THRESHOLD}`
      : `Soft bounce ${softCount}/${SOFT_BOUNCE_THRESHOLD}\nWill auto-suppress at ${SOFT_BOUNCE_THRESHOLD}.\nLast event: ${suppression?.last_event_at ? new Date(suppression.last_event_at).toLocaleString() : "unknown"}`;
    return (
      <span
        title={title}
        className="inline-flex items-center gap-1 rounded-md bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 border border-yellow-200"
      >
        <svg
          className="h-3 w-3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.515 2.625H3.72c-1.345 0-2.188-1.458-1.515-2.625l6.28-10.875zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
        Email soft bounce {softCount}/{SOFT_BOUNCE_THRESHOLD}
      </span>
    );
  }

  return null;
};

export default EmailSuppressionBadge;
